import { useState } from "react";
import { Plus, Search, X, Pencil, Check, Trash2 } from "lucide-react";
import { useAppData } from "../../lib/AppDataContext";
import { mediaApi } from "../../lib/mediaApi";
import { Card } from "../../components/shared/Card";
import { PageHeader } from "../../components/shared/PageHeader";
import { Btn } from "../../components/shared/Btn";
import { TagInput } from "../../components/shared/TagInput";
import { Modal } from "../../components/shared/Modal";
import { toast } from "../../components/shared/Toast";

function slugify(t) {
  return String(t || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
}

const EMPTY = {
  name: "", logoUrl: "", wordmarkText: "", wordmarkTagline: "",
  experience: "", totalProjects: "", happyFamilies: "", description: "",
  address: "", gst: "", builderNameOnBill: "",
};

export function BuildersView() {
  const { builders, mastersLoading, addBuilder, updateBuilder, deleteBuilder } = useAppData();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);   // id when editing, null when adding/closed
  const [form, setForm] = useState(EMPTY);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleting, setDeleting] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleLogo(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast("Please upload an image file"); return; }
    setUploadingLogo(true);
    try {
      const res = await mediaApi.uploadImage(file);
      if (!res?.url) throw new Error("Upload returned no URL");
      setField("logoUrl", res.url);
    } catch (err) {
      toast(err?.message || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  }

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY);
    setLocations([]);
    setAdding(true);
  };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      name: b.name || "",
      logoUrl: b.logoUrl || "",
      wordmarkText: b.wordmarkText || "",
      wordmarkTagline: b.wordmarkTagline || "",
      experience: b.experience || "",
      totalProjects: b.totalProjects ?? "",
      happyFamilies: b.happyFamilies || "",
      description: b.description || "",
      address: b.address || "",
      gst: b.gst || "",
      builderNameOnBill: b.builderNameOnBill || "",
    });
    setLocations(Array.isArray(b.locations) ? b.locations : []);
    setAdding(true);
  };
  const closeForm = () => { setAdding(false); setEditingId(null); setForm(EMPTY); setLocations([]); };

  const save = async () => {
    if (!form.name.trim()) { toast("Builder name is required"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      slug: slugify(form.name),
      logoUrl: form.logoUrl || "",
      wordmarkText: form.wordmarkText.trim(),
      wordmarkTagline: form.wordmarkTagline.trim(),
      experience: form.experience.trim(),
      /* Text, not a number. The client needs "25+" and similar, the same
         way happyFamilies has always worked; Number() turned that into NaN
         and the value vanished. Nothing sorts or sums it -- the customer
         site's two consumers only display it. */
      totalProjects: form.totalProjects === "" || form.totalProjects == null ? null : String(form.totalProjects),
      happyFamilies: form.happyFamilies.trim(),
      locations: [...locations],
      description: form.description.trim(),
      address: form.address.trim(),
      gst: form.gst.trim(),
      builderNameOnBill: form.builderNameOnBill.trim(),
    };
    try {
      if (editingId) { await updateBuilder(editingId, payload); toast("Builder updated"); }
      else { await addBuilder(payload); toast("Builder added"); }
      closeForm();
    } catch (err) {
      toast(err?.message || "Failed to save builder");
    } finally {
      setSaving(false);
    }
  };

  if (mastersLoading) {
    return <div className="p-6"><Card className="p-10 text-center text-slate-500 text-sm">Loading…</Card></div>;
  }

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? builders.filter((b) =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.wordmarkText || "").toLowerCase().includes(q))
    : builders;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader accent="inventory"
        title="Builders"
        subtitle={`${builders.length} developer${builders.length === 1 ? "" : "s"}`}
        actions={<Btn variant="primary" onClick={() => (adding ? closeForm() : openAdd())}><Plus className="w-4 h-4" />Add Builder</Btn>}
      />

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by builder name"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 pl-8 pr-8 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Clear search">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Add / Edit form */}
      {adding && (
        <Modal
          title={editingId ? "Edit builder" : "New builder"}
          onClose={closeForm}
          size="lg"
          dismissOnBackdrop={false}
        >
        <div className="p-5 sm:p-6 max-h-[75vh] min-h-0 overflow-y-auto">

          <div className="mb-4">
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Logo / icon</label>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 shrink-0 rounded-lg ring-1 ring-inset ring-slate-200 bg-white grid place-items-center overflow-hidden">
                {form.logoUrl
                  ? <img src={form.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                  : <span className="text-[10px] text-slate-400 text-center px-1">No logo</span>}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="inline-flex items-center gap-2 text-sm text-slate-600 rounded-lg ring-1 ring-dashed ring-slate-300 px-3 py-2 hover:bg-slate-50 cursor-pointer w-fit">
                  {uploadingLogo ? "Uploading…" : (form.logoUrl ? "Replace image" : "Upload image")}
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo} onChange={(e) => handleLogo(e.target.files?.[0])} />
                </label>
                {form.logoUrl && (
                  <button type="button" onClick={() => setField("logoUrl", "")} className="text-xs text-red-600 hover:underline w-fit">Remove logo</button>
                )}
                <p className="text-[11px] text-slate-400">Shown on the customer builder card. PNG/SVG/JPG.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Builder Group Name <span className="text-red-600">*</span></label>
              <input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Godrej Properties"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Wordmark text</label>
              <input value={form.wordmarkText} onChange={(e) => setField("wordmarkText", e.target.value)} placeholder="e.g. GODREJ"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Wordmark tagline</label>
              <input value={form.wordmarkTagline} onChange={(e) => setField("wordmarkTagline", e.target.value)} placeholder="e.g. PROPERTIES"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Experience</label>
              <input value={form.experience} onChange={(e) => setField("experience", e.target.value)} placeholder="e.g. 27+ yrs"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Total projects</label>
              <input type="text" value={form.totalProjects} onChange={(e) => setField("totalProjects", e.target.value)} placeholder="e.g. 89 or 25+"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Happy families</label>
              <input value={form.happyFamilies} onChange={(e) => setField("happyFamilies", e.target.value)} placeholder="e.g. 12,000+"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Locations</label>
            <TagInput tags={locations} setTags={setLocations} placeholder="Type a city and press Enter…" />
          </div>
          <div className="mt-4">
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Description (about)</label>
            <textarea value={form.description} onChange={(e) => setField("description", e.target.value)} rows={4} placeholder="Write the builder's about text as it appears on the customer builder card…"
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Address <span className="text-slate-400 font-normal">(builder bill only)</span>
              </label>
              <textarea value={form.address} onChange={(e) => setField("address", e.target.value)} rows={2}
                placeholder="Full registered address; commas OK"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                GST no. <span className="text-slate-400 font-normal">(builder bill only)</span>
              </label>
              <input value={form.gst} onChange={(e) => setField("gst", e.target.value)}
                placeholder="e.g. 27AAMCB0412C1Z9"
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-200 mb-2">
              Builder name on bill <span className="text-slate-400 font-normal">(if different from company name)</span>
            </label>
            <input value={form.builderNameOnBill} onChange={(e) => setField("builderNameOnBill", e.target.value)}
              placeholder="e.g. BEYONDWALLS (short name for bills)"
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={closeForm}>Cancel</Btn>
            <Btn variant="primary" onClick={save} disabled={saving}><Check className="w-4 h-4" />{saving ? "Saving…" : "Save builder"}</Btn>
          </div>
        </div>
        </Modal>
      )}

      {/* List as cards */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-400">
          {builders.length === 0 ? "No builders yet." : `No builders match \u201c${searchQuery}\u201d.`}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((b) => (
            <Card key={b.id} tone="gold" className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{b.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {[b.wordmarkText, b.wordmarkTagline].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(b)} className="text-slate-400 hover:text-slate-700 p-1" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setDeleting(b)} className="text-slate-400 hover:text-red-600 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {Array.isArray(b.locations) && b.locations.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {b.locations.map((loc) => (
                    <span key={loc} className="text-xs font-medium bg-slate-100 text-slate-600 rounded-md px-2 py-0.5">{loc}</span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-4 sm:gap-6 mt-3 pt-3 border-t border-slate-100">
                <div><div className="text-xs text-slate-400">Experience</div><div className="text-sm font-semibold text-slate-900">{b.experience || "—"}</div></div>
                <div><div className="text-xs text-slate-400">Projects</div><div className="text-sm font-semibold text-slate-900 tabular-nums">{b.totalProjects || "\u2014"}</div></div>
                <div><div className="text-xs text-slate-400">Happy families</div><div className="text-sm font-semibold text-slate-900">{b.happyFamilies || "—"}</div></div>
              </div>

              {b.description && <p className="text-xs text-slate-500 leading-relaxed mt-3 line-clamp-3">{b.description}</p>}
            </Card>
          ))}
        </div>
      )}

      {deleting && (
        <Modal onClose={() => setDeleting(null)} title="Delete builder?" size="sm">
          <div className="p-4 space-y-3 text-sm">
            <p>Soft-delete <strong>{deleting.name}</strong>?</p>
            <p className="text-xs text-amber-700">Only allowed if no active properties depend on this builder.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setDeleting(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={async () => {
                try { await deleteBuilder(deleting.id); toast("Builder deleted"); setDeleting(null); }
                catch (err) { toast(err?.message || "Failed to delete"); }
              }}>Delete</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
